import _ from 'lodash';
import { createSelector, createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
	Pay,
	payIntialState,
	payStepsStatus,
	paySteps,
	PayId,
	PayDetails,
	PayPreference,
	PayReason,
	PaymentFees,
	paymentsTypes,
} from 'constants/pay.constants';
import { formValue, intialMessage, userMessage } from 'constants/ui.constants';

interface step {
	status: payStepsStatus;
}

type steps = {
	[key in paySteps]: step;
};

type obj = { [key: string]: string };

interface payState {
	items: Pay[];
	loading: boolean;
	message: userMessage;
	upsertingProcess: boolean;
	upsertedPay: Pay;
	activeStep: paySteps;
	steps: Partial<steps>;
	paymentFees: PaymentFees;
	paymentDates: string[];
	paymentFeesLoading: boolean;
	paymentFeesError: boolean;
}

const intialStep = { status: payStepsStatus.notStarted };

const intialSteps = () => {
	let s: Partial<steps> = {};
	_.values(paySteps).forEach((k) => {
		s[k] = intialStep;
	});
	return s;
};

const initialState: payState = {
	items: [],
	loading: false,
	message: intialMessage,
	upsertingProcess: false,
	upsertedPay: payIntialState,
	activeStep: paySteps.BENEFICIARY_DETAILS,
	steps: intialSteps(),
	paymentFees: {},
	paymentDates: [],
	paymentFeesLoading: false,
	paymentFeesError: false,
};

export const paySlice = createSlice({
	name: 'pay',
	initialState,
	reducers: {
		setLoading: (state, action: PayloadAction<boolean>) => {
			state.loading = action.payload;
		},
		setMessage: (state, action: PayloadAction<userMessage>) => {
			state.message = action.payload;
		},
		toggleUpserting: (state, action: PayloadAction<boolean>) => {
			state.upsertingProcess = action.payload;
			state.upsertedPay = payIntialState;
			state.steps = intialSteps();
			state.activeStep = paySteps.BENEFICIARY_DETAILS;
		},
		setPaymentDates: (state, action: PayloadAction<string[]>) => {
			state.paymentDates = action.payload;
		},
		intializePay: (state, action: PayloadAction<Pay>) => {
			state.upsertedPay = action.payload;
		},
		setSteps: (state, action: PayloadAction<Partial<steps>>) => {
			state.steps = { ...state.steps, ...action.payload };
		},
		setActiveStep: (state, action: PayloadAction<paySteps>) => {
			state.activeStep = action.payload;
		},
		upsertPay: (state, action: PayloadAction<Partial<Pay>>) => {
			state.upsertedPay = {
				...state.upsertedPay,
				...action.payload,
			};
		},
		upsertPayBeneficiary: (state, action: PayloadAction<Partial<Pay>>) => {
			const obj = action.payload.beneficiary;
			let payType = obj?.isLocalPaymentAllowed
				? obj?.isPriorityPaymentAllowed
					? undefined
					: paymentsTypes.LOCAL
				: obj?.isLocalPaymentAllowed
				? undefined
				: paymentsTypes.PRIORITY;

			state.upsertedPay = {
				...state.upsertedPay,
				...action.payload,
				paymentType: payType,
			};
		},
		setPayementFees: (state, action: PayloadAction<PaymentFees>) => {
			state.paymentFees = action.payload;
		},
		setPay: (state, action: PayloadAction<formValue>) => {
			if (action.payload.name.includes('.')) {
				const path = action.payload.name.split('.');
				if (_.has(state.upsertedPay, path[0])) {
					let nested = _.get(state.upsertedPay, path[0]);
					state.upsertedPay = {
						...state.upsertedPay,
						//@ts-ignore
						[path[0] as keyof Pay]: { ...nested, [path[1]]: action.payload.value },
					};
				} else {
					state.upsertedPay = {
						...state.upsertedPay,
						[path[0] as keyof Pay]: { [path[1]]: action.payload.value },
					};
				}
			} else {
				state.upsertedPay = {
					...state.upsertedPay,
					[action.payload.name]: action.payload.value,
				};
			}
		},
		setPaymentFeesLoading(state, action) {
			state.paymentFeesLoading = action.payload;
		  },
		  setPaymentFeesError(state, action) {
			state.paymentFeesError = action.payload;
		  },
	},
});

export const {
	setLoading,
	setMessage,
	setSteps,
	setActiveStep,
	toggleUpserting,
	intializePay,
	upsertPay,
	upsertPayBeneficiary,
	setPay,
	setPayementFees,
	setPaymentDates,
	setPaymentFeesLoading, 
	setPaymentFeesError
} = paySlice.actions;

const paySelector = (state) => state.pay;
export const isLoading = (state: any) => state.pay.loading;
export const getMessage = (state: any) => state.pay.message;
export const isCreatingProcess = (state: any) => state.pay.upsertingProcess;
export const getNewPay = (state: any) => state.pay.upsertedPay;
export const getSteps = (state: any) => state.pay.steps;
export const getActiveStep = (state: any) => {
	return _.indexOf(_.values(paySteps), state.pay.activeStep);
};

export const getPays = (state: any) => {
	return state.pay.items;
};

export const getPayById = (id: string) => (state: any) => {
	return _.find(state.pay.items, { [PayId]: id });
};

export const getPayDetails = createSelector([paySelector], (state: any): obj => {
	let obj: obj = {};
	let bn = state.upsertedPay;
	PayDetails.forEach((key) => (obj[key] = bn[key] ? String(bn[key]) : ''));
	return obj;
});

export const getPayPreference = createSelector([paySelector], (state: any): obj => {
	let obj: obj = {};
	let bn = state.upsertedPay;
	PayPreference.forEach((key) => (obj[key] = bn[key] ? String(bn[key]) : ''));
	return obj;
});

export const getPayReason = createSelector([paySelector], (state: any): obj => {
	let obj: obj = {};
	let bn = state.upsertedPay;
	PayReason.forEach((key) => (obj[key] = bn[key] ? String(bn[key]) : ''));
	return obj;
});

export const getPaymentDates = (state: any): any => {
	return state.pay.paymentDates;
};

export const getPayFees = (state: any): obj => {
	return state.pay.paymentFees;
};

export default paySlice.reducer;
